import React, { useEffect, useState } from "react";
import { Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@Constants/routes";
import { Switch } from "@Components/shadcn/ui/switch";
import { useUpdateAutomationStatusMutation } from "../../apiSlice";
import { formatTimestampDifference } from "@Utils/common"


interface AutomationTableInterface {
    data: Array<any>,
    openDeleteAutomationModal: (id:number) => void,
    fetchAutomations: ()=>void
  }

const AutomationTable = (props: AutomationTableInterface) => {

    const navigate = useNavigate()

    let [UpdateAutomationStatus, { data: UpdateAutomationStatusData, error: UpdateAutomationStatusError, isLoading: isUpdateAutomationStatusLoading }] = useUpdateAutomationStatusMutation()

    const handleSwitchOnCheck = (id:number, status:boolean) => {
        UpdateAutomationStatus({
            automation_id: id, status
        })
    }

    useEffect(() => {
        if (UpdateAutomationStatusData) {
            props.fetchAutomations()
        }
    }, [UpdateAutomationStatusData, UpdateAutomationStatusError])
    

    return(
        <div className="w-full">
            <div className="bg-white rounded-lg border border-gray-100">
                {/* Desktop table with hidden md:table class */}
                <table className="w-full hidden md:table">
                    <thead>
                        <tr className="border-b border-gray-100">
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Name</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Triggered</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Created At</th>
                            <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Last Modified</th>
                            <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props?.data && props?.data.length > 0 ? (
                            props.data.map((automation: any) => (
                                <tr key={automation.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                                    <td className="px-6 py-2">
                                        <p className="text-[15px] text-blue-600 hover:text-blue-700 font-medium hover:cursor-pointer"
                                           onClick={() => navigate(ROUTES.FLOW_BUILDER.replace(':automation_doc_id', automation.id))}>
                                            {automation.name}
                                        </p>
                                    </td>
                                    <td className="px-6 py-2">
                                        <p className="text-[15px] text-gray-900 my-0">{automation.triggered}</p>
                                    </td>
                                    <td className="px-6 py-2">
                                        <div className={`inline-flex px-3 py-1 rounded-full text-[13px] font-medium ${automation?.status ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                            {automation?.status ? 'Active' : 'Inactive'}
                                        </div>
                                    </td>
                                    <td className="px-6 py-2">
                                        <p className="text-[15px] text-gray-900 my-0">{formatTimestampDifference(automation.created_at)}</p>
                                    </td>
                                    <td className="px-6 py-2">
                                        <p className="text-[15px] text-gray-900 my-0">{formatTimestampDifference(automation.updated_at)}</p>
                                    </td>
                                    <td className="px-6 py-2 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => navigate(ROUTES.FLOW_BUILDER.replace(':automation_doc_id', automation.id))}
                                                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg transition-colors"
                                            >
                                                <Pencil1Icon className="w-[18px] h-[18px]" />
                                            </button>
                                            <button
                                                onClick={() => props.openDeleteAutomationModal(automation.id)}
                                                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg transition-colors"
                                            >
                                                <TrashIcon className="w-[18px] h-[18px]" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={6} className="px-6 py-8 text-center">
                                    <p className="text-gray-500 text-[15px]">No automations found</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {/* Mobile table */}
                <table className="w-full md:hidden">
                    <thead>
                        <tr className="border-b border-gray-100">
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Name</th>
                            <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Status</th>
                            <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {props?.data && props?.data.length > 0 ? (
                            props.data.map((automation: any) => (
                                <tr key={`mobile-${automation.id}`} className="border-b border-gray-50">
                                    <td className="px-4 py-3">
                                        <div className="space-y-1">
                                            <p className="text-[15px] text-blue-600 hover:text-blue-700 font-medium hover:cursor-pointer"
                                               onClick={() => navigate(ROUTES.FLOW_BUILDER.replace(':automation_doc_id', automation.id))}>
                                                {automation.name}
                                            </p>
                                            <p className="text-[13px] text-gray-500">Triggered: {automation.triggered}</p>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className={`inline-flex px-3 py-1 rounded-full text-[13px] font-medium ${automation?.status ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                                            {automation?.status ? 'Active' : 'Inactive'}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                        <div className="flex justify-end gap-2">
                                            <button
                                                onClick={() => navigate(ROUTES.FLOW_BUILDER.replace(':automation_doc_id', automation.id))}
                                                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg transition-colors"
                                            >
                                                <Pencil1Icon className="w-[18px] h-[18px]" />
                                            </button>
                                            <button
                                                onClick={() => props.openDeleteAutomationModal(automation.id)}
                                                className="p-2 text-gray-500 hover:text-gray-700 rounded-lg transition-colors"
                                            >
                                                <TrashIcon className="w-[18px] h-[18px]" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan={3} className="px-4 py-6 text-center">
                                    <p className="text-gray-500 text-[15px]">No automations found</p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                <div className="px-6 py-4 border-t border-gray-100">
                    <div className="flex items-center text-sm text-gray-500">
                        <span>Page length: </span>
                        <span className="ml-2 font-medium text-gray-900">10</span>
                    </div>
                </div>
            </div>
        </div>
    )
} 

export default AutomationTable